import React from 'react';
import { Container, Typography, Box, Divider } from '@mui/material';

const PrivacyPolicy = () => {
  const accountDeletionUrl = 'https://prathna.maverickthinkerspvtltd.com/delete-account';

  const Section = ({ number, title, children }) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
        {number}. {title}
      </Typography>
      {children}
    </Box>
  );

  const Para = ({ children }) => (
    <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
      {children}
    </Typography>
  );

  const SubHeading = ({ children }) => (
    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 0.5 }}>
      {children}
    </Typography>
  );

  const BulletList = ({ items }) => (
    <Box component="ul" sx={{ mt: 0.5, pl: 3 }}>
      {items.map((item, i) => (
        <Typography key={i} component="li" variant="body1" sx={{ mb: 0.5 }}>
          {item}
        </Typography>
      ))}
    </Box>
  );

  const OrderedList = ({ items }) => (
    <Box component="ol" sx={{ mt: 0.5, pl: 3 }}>
      {items.map((item, i) => (
        <Typography key={i} component="li" variant="body1" sx={{ mb: 0.5 }}>
          {item}
        </Typography>
      ))}
    </Box>
  );

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
        PRIVACY POLICY
      </Typography>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
        Prarthana: Pray for Others
      </Typography>
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2"><strong>Operated by:</strong> Maverick Thinkers Pvt. Ltd.</Typography>
        <Typography variant="body2"><strong>Effective Date:</strong> 23 October 2026</Typography>
        <Typography variant="body2"><strong>Last Updated:</strong> 2 November 2026</Typography>
      </Box>
      <Divider sx={{ mb: 4 }} />

      {/* Section 1 */}
      <Section number={1} title="Introduction">
        <Para>
          Welcome to Prarthana: Pray for Others ("Prarthana", "App", "we", "our" or "us").
        </Para>
        <Para>
          Prarthana is a platform that encourages people to pray for others who may be going through difficult situations. People from all religions and faiths are welcome to participate and offer prayers according to their own beliefs and traditions.
        </Para>
        <Para>
          The App allows users to submit prayer requests and enables other registered users to express their willingness to pray for the person concerned.
        </Para>
        <Para>
          Prarthana is operated by Maverick Thinkers Pvt. Ltd.
        </Para>
        <Para>
          We respect your privacy and are committed to protecting the personal information you share with us. This Privacy Policy explains what information we collect, why we collect it, how we use it, when it may be shared and what choices you have.
        </Para>
        <Para>
          By using Prarthana, you acknowledge that you have read this Privacy Policy.
        </Para>
      </Section>

      {/* Section 2 */}
      <Section number={2} title="Information We Collect">
        <Para>We collect information necessary to operate the App and provide its services.</Para>

        <SubHeading>A. Account Information</SubHeading>
        <Para>When you create an account, we collect:</Para>
        <BulletList items={['Your name.', 'Your mobile number.']} />
        <Para>
          Your mobile number may be used for account verification, authentication and important service-related communication.
        </Para>

        <SubHeading>B. Prayer Request Information</SubHeading>
        <Para>
          When you submit a prayer request, we collect the following mandatory information about the person for whom the prayer is requested:
        </Para>
        <BulletList items={['Name.', 'Gotra.', 'Residential area.', 'City.']} />
        <Para>
          These details are required as part of the prayer-request process and its intended religious and cultural practice. Anonymous prayer requests are not supported.
        </Para>
        <Para>
          Depending on the information you choose to provide, a prayer request may also contain:
        </Para>
        <BulletList items={[
          'A photograph.',
          'A description of the situation.',
          'Health-related or other personal details relevant to the request.',
        ]} />
        <Para>Please avoid sharing unnecessary or highly sensitive information.</Para>

        <SubHeading>C. Participation and Comments</SubHeading>
        <Para>When you participate in a prayer request, we may collect:</Para>
        <BulletList items={[
          'Your participation or willingness to pray.',
          'Comments or messages you submit.',
          'Other information associated with your interaction with the App.',
        ]} />

        <SubHeading>D. Technical and Device Information</SubHeading>
        <Para>
          Depending on the services and technologies implemented in the App, we or our service providers may collect technical information such as:
        </Para>
        <BulletList items={[
          'Device type and operating system.',
          'IP address.',
          'App version.',
          'Device identifiers.',
          'Crash reports and diagnostic information.',
          'App usage and interaction information.',
        ]} />
        <Para>
          The exact information collected will depend on the App configuration and the third-party services integrated into it.
        </Para>
      </Section>

      {/* Section 3 */}
      <Section number={3} title="Information About Children and Other Vulnerable Individuals">
        <Para>
          Prarthana may be used to request prayers for children and individuals who may be unable to provide consent themselves.
        </Para>
        <Para>
          If you submit a prayer request containing personal information about a child or an individual who cannot provide consent, you must have the necessary authority or permission from their parent, guardian or legally authorised representative.
        </Para>
        <Para>We may require additional verification or consent where applicable.</Para>
        <Para>
          We do not intend to collect children's information without the necessary permissions and safeguards required by applicable law.
        </Para>
        <Para>
          We do not use children's personal information for targeted advertising or behavioural profiling.
        </Para>
      </Section>

      {/* Section 4 */}
      <Section number={4} title="Consent Before Sharing Someone Else's Information">
        <Para>You may submit a prayer request for yourself or another person.</Para>
        <Para>
          If you submit information about another individual, you confirm that you have obtained the necessary permission or legal authority to share that information through Prarthana.
        </Para>
        <Para>This applies to:</Para>
        <BulletList items={[
          'Name.',
          'Gotra.',
          'Residential area and city.',
          'Photographs.',
          'Health-related information.',
          'Any other personal information included in the request.',
        ]} />
        <Para>
          If you upload a photograph, you must separately confirm that you have permission to share it.
        </Para>
        <Para>
          We may ask for additional information or remove a prayer request if we believe that personal information has been shared without appropriate permission.
        </Para>
      </Section>

      {/* Section 5 */}
      <Section number={5} title="How We Use Your Information">
        <Para>We use the information collected for purposes including:</Para>
        <BulletList items={[
          'Creating and managing user accounts.',
          'Verifying users and securing accounts.',
          'Enabling users to submit prayer requests.',
          'Displaying prayer requests to registered users.',
          'Allowing users to express their willingness to pray.',
          'Facilitating comments and participation.',
          'Reviewing and moderating prayer requests.',
          'Preventing misuse, spam, harassment and fraudulent activity.',
          'Responding to user complaints and support requests.',
          'Maintaining, securing and improving the App.',
          'Meeting legal and regulatory requirements.',
          'Displaying advertisements, where enabled, through advertising partners.',
        ]} />
        <Para>We do not sell users' personal information as a business activity.</Para>
      </Section>

      {/* Section 6 */}
      <Section number={6} title="Visibility of Prayer Requests">
        <Para>
          Prayer requests submitted through Prarthana are intended to be visible only to registered users of the App.
        </Para>
        <Para>They are not intended to be publicly accessible to people who have not registered.</Para>
        <Para>
          However, registered users may be able to view the information included in a prayer request and may potentially copy, record or share that information outside the App.
        </Para>
        <Para>
          We cannot guarantee that information viewed by another user will remain confidential after that user has accessed it.
        </Para>
        <Para>
          We therefore encourage users to share only information they are authorised to share and that is genuinely necessary for the prayer request.
        </Para>
        <Para>
          We will take reasonable technical measures to prevent unauthorised public access to prayer requests.
        </Para>
      </Section>

      {/* Section 7 */}
      <Section number={7} title="Manual Review and Moderation">
        <Para>
          We may manually review prayer requests and user-submitted content to maintain a respectful and safe environment.
        </Para>
        <Para>
          We may remove content, restrict access, suspend accounts or permanently block users who violate our Terms and Conditions or misuse the App.
        </Para>
        <Para>
          Users may report content they believe violates privacy, consent requirements or community rules.
        </Para>
      </Section>

      {/* Section 8 */}
      <Section number={8} title="How We Share Information">
        <SubHeading>A. With Registered Users</SubHeading>
        <Para>
          Prayer request information is made available to registered Prarthana users so that they can participate in prayer activities.
        </Para>

        <SubHeading>B. With Service Providers</SubHeading>
        <Para>
          We may share relevant information with trusted service providers who help us operate the App, provide hosting, store information, maintain security, deliver notifications, verify accounts or provide technical support.
        </Para>
        <Para>
          These providers may process information on our behalf and are expected to protect it in accordance with applicable contractual and legal requirements.
        </Para>

        <SubHeading>C. With Advertising Partners</SubHeading>
        <Para>We have selected Google AdMob as our advertising service provider.</Para>
        <Para>
          When advertisements are enabled, Google AdMob and related services may collect or process certain information, such as device identifiers, IP address, advertising interactions and other technical information, depending on the advertising configuration and applicable consent choices.
        </Para>
        <Para>
          Advertising-related data practices are governed by Google's applicable privacy terms and policies.
        </Para>
        <Para>
          We will not use private prayer-request information, including religious details, gotra, health information or mental-health information, to create advertising audiences or target advertisements.
        </Para>

        <SubHeading>D. Legal and Safety Requirements</SubHeading>
        <Para>
          We may disclose information when required by law, legal process, court order or a lawful request from an authorised government authority.
        </Para>
        <Para>
          We may also disclose information where reasonably necessary to protect users, prevent fraud, investigate misuse or protect our legal rights.
        </Para>

        <SubHeading>E. Business Transfers</SubHeading>
        <Para>
          If Maverick Thinkers Pvt. Ltd. undergoes a merger, acquisition, restructuring or transfer of business assets, relevant information may be transferred as part of that transaction, subject to applicable privacy laws.
        </Para>
      </Section>

      {/* Section 9 */}
      <Section number={9} title="Advertising">
        <Para>
          Prarthana may display advertisements to support the operation and maintenance of the App and generate revenue for the company.
        </Para>
        <Para>We use Google AdMob for advertising services.</Para>
        <Para>
          Advertising may involve the collection or processing of technical information and device identifiers, depending on the SDK configuration, consent requirements and applicable settings.
        </Para>
        <Para>
          We do not permit the use of private prayer-request information, religious details, health information or mental-health information for targeted advertising.
        </Para>
        <Para>
          We will update this Privacy Policy if our advertising practices or providers materially change.
        </Para>
      </Section>

      {/* Section 10 */}
      <Section number={10} title="Data Storage and Security">
        <Para>
          We have selected Amazon Web Services (AWS) for cloud infrastructure and data-storage services.
        </Para>
        <Para>
          Information collected through the App may be stored or processed using AWS services.
        </Para>
        <Para>
          The exact AWS services, hosting region and storage configuration will depend on the technical implementation of the App.
        </Para>
        <Para>We intend to use reasonable security measures, which may include:</Para>
        <BulletList items={[
          'Encryption of information during transmission and, where supported, at rest.',
          'Access controls and restricted administrative permissions.',
          'Secure authentication.',
          'Monitoring and logging.',
          'Regular backups.',
          'Measures to prevent unauthorised access, alteration, disclosure or destruction of information.',
        ]} />
        <Para>
          No internet-based service can guarantee absolute security. We will take reasonable steps to protect the information entrusted to us.
        </Para>
      </Section>

      {/* Section 11 */}
      <Section number={11} title="Data Retention and Deletion">
        <Para>
          We retain personal information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, enforce our agreements and protect the App.
          For account deletion, please contact us via email at <a href="mailto:alok@maverickthinkers.in">alok@maverickthinkers.in</a>.
        </Para>
        <Para>
          After verifying a deletion request, we will delete the account and associated personal information from our active systems, subject to applicable legal requirements.
        </Para>
        <Para>
          Some information may need to be retained for a limited period where required by law or reasonably necessary for security, fraud prevention, dispute resolution or compliance.
        </Para>
        <Para>
          Information remaining in backups may be removed according to our documented backup-retention and deletion schedule. Such information will remain protected and will not be used for ordinary service purposes.
        </Para>
      </Section>

      {/* Section 12 */}
      <Section number={12} title="Your Rights and Choices">
        <Para>Subject to applicable law, you may have the right to:</Para>
        <BulletList items={[
          'Request access to your personal information.',
          'Request correction of inaccurate or incomplete information.',
          'Request deletion of your personal information.',
          'Withdraw consent where processing is based on consent.',
          'Raise concerns about the use of your personal information.',
          'Submit a grievance or privacy-related complaint.',
        ]} />
        <Para>
          You may contact us using the details provided in this Privacy Policy to exercise these rights.
        </Para>
      </Section>

      {/* Section 13 */}
      <Section number={13} title="Withdrawal of Consent">
        <Para>
          Where we process information based on your consent, you may withdraw that consent by contacting us or using the relevant options available in the App.
        </Para>
        <Para>
          Withdrawal of consent will not affect processing that took place lawfully before the withdrawal.
        </Para>
        <Para>
          Please note that withdrawing consent may affect your ability to use certain features or services.
        </Para>
      </Section>

      {/* Section 14 */}
      <Section number={14} title="Third-Party Services">
        <Para>
          Prarthana may use third-party services to provide hosting, storage, advertising, authentication, notifications, analytics, crash reporting and other technical functions.
        </Para>
        <Para>Currently selected providers include:</Para>
        <BulletList items={[
          'Amazon Web Services (AWS) for cloud infrastructure and storage.',
          'Google AdMob for advertising.',
        ]} />
        <Para>Other providers may be integrated as the App develops.</Para>
        <Para>
          These third-party services may process information according to their own privacy policies and terms.
        </Para>
        <Para>We encourage users to review the privacy policies of relevant service providers.</Para>
      </Section>

      {/* Section 15 */}
      <Section number={15} title="International Data Transfers">
        <Para>
          Depending on the AWS hosting region and the configuration of third-party services, information may be processed or stored outside India.
        </Para>
        <Para>
          Where international transfers occur, we will take steps to ensure that such transfers comply with applicable Indian data-protection laws and other relevant legal requirements.
        </Para>
      </Section>

      {/* Section 16 */}
      <Section number={16} title="Reporting Privacy Concerns">
        <Para>
          If you believe that someone has shared your personal information without permission, uploaded a photograph without appropriate consent or misused information available through Prarthana, you may contact us.
        </Para>
        <Para>
          We will review complaints and take appropriate action, which may include removing content, restricting access or suspending the account responsible.
        </Para>
      </Section>

      {/* Section 17 */}
      <Section number={17} title="Changes to This Privacy Policy">
        <Para>We may update this Privacy Policy from time to time to reflect changes in:</Para>
        <BulletList items={[
          'App features.',
          'Data collection and processing practices.',
          'Technology and service providers.',
          'Advertising practices.',
          'Applicable laws and regulations.',
        ]} />
        <Para>
          The updated version will be published through the App and/or our website with a revised "Last Updated" date.
        </Para>
        <Para>
          Where required by law, we will obtain fresh consent or provide additional notice before implementing material changes.
        </Para>
      </Section>

      {/* Section 18 */}
      <Section number={18} title="Contact and Grievance Redressal">
        <Para>
          For privacy-related questions, requests, complaints or concerns, please contact:
        </Para>
        <Box sx={{ pl: 2, borderLeft: '3px solid', borderColor: 'divider', mb: 2 }}>
          <Typography variant="body1"><strong>Company:</strong> Maverick Thinkers Pvt. Ltd.</Typography>
          <Typography variant="body1"><strong>App:</strong> Prarthana: Pray for Others</Typography>
          <Typography variant="body1"><strong>Email:</strong> alok@maverickthinkers.in</Typography>
          <Typography variant="body1"><strong>Registered Office:</strong> 201, Shri Kalyanam Residency, Nandlalpura Main Road, Indore</Typography>
        </Box>
        <Para>
          We will handle privacy-related requests and grievances in accordance with applicable laws.
        </Para>
      </Section>

      {/* Section 19 */}
      <Section number={19} title="Final Statement">
        <Para>
          Prarthana is built on the belief that people can come together to offer prayers and goodwill for others, regardless of religion, background or personal circumstances.
        </Para>
        <Para>
          We recognise that the information shared through prayer requests can be personal and sensitive. We are committed to handling such information responsibly and taking reasonable steps to protect users' privacy.
        </Para>
        <Para>
          By using Prarthana, you acknowledge that you have read and understood this Privacy Policy.
        </Para>
      </Section>
    </Container>
  );
};

export default PrivacyPolicy;
